import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { backendAPI } from '../api/backend';
import { useNavigationStack } from '../contexts/NavigationContext';
import { handleImageError } from '../utils/imageUtils';
import './Favorites.css';

const Favorites = () => {
  const navigate = useNavigate();
  const { navigateWithTransition } = useNavigationStack();
  const { currentUser } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    if (!currentUser) {
      navigate('/auth');
      return;
    }
    loadFavorites();
  }, [currentUser, navigate]);

  const loadFavorites = async () => {
    try {
      setLoading(true);
      const profile = await backendAPI.getUserProfile();
      setFavorites(profile.user.favorite_items || []);
    } catch (err) {
      console.error('Failed to load favorites:', err);
      setError('Unable to load favorites. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleRemoveFavorite = async (itemUrl) => {
    try {
      await backendAPI.removeFavorite(itemUrl);
      setFavorites(prev => prev.filter(item => item.url !== itemUrl));
      showNotification('Removed from favorites', 'info');
    } catch (err) {
      console.error('Failed to remove favorite:', err);
      showNotification('Failed to remove item', 'error');
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  if (loading) {
    return (
      <div className="favorites-page">
        <div className="favorites-loading">
          <div className="loading-spinner"></div>
          <p>Loading your favorites...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="favorites-page">
      <div className="favorites-container">
        <motion.div
          className="favorites-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Link to="/" className="back-button" aria-label="Go back to home">
            ← Back to Home
          </Link>
          <h1 className="favorites-title">My Favorites</h1>
          <p className="favorites-subtitle">
            {favorites.length === 0
              ? 'No favorites yet. Start exploring and save items you love!'
              : `${favorites.length} item${favorites.length === 1 ? '' : 's'} saved`}
          </p>
        </motion.div>

        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}

        {favorites.length === 0 ? (
          <motion.div
            className="empty-state"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="empty-icon">❤️</div>
            <h2>No Favorites Yet</h2>
            <p>Start searching for fashion items and click the heart icon to save your favorites!</p>
            <Link to="/search" className="cta-button">
              Start Searching
            </Link>
          </motion.div>
        ) : (
          <motion.div
            className="favorites-grid"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {favorites.map((item, index) => (
              <motion.div
                key={item.url || index}
                className="favorite-card"
                variants={itemVariants}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
              >
                <div className="favorite-image-wrapper">
                  {item.image && (
                    <img
                      src={item.image}
                      alt={item.name || 'Favorite item'}
                      className="favorite-image"
                      data-original-src={item.image}
                      onError={(e) => handleImageError(e, item.image)}
                    />
                  )}
                  <button
                    className="remove-favorite-btn"
                    onClick={() => handleRemoveFavorite(item.url)}
                    aria-label="Remove from favorites"
                    title="Remove from favorites"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
                <div className="favorite-content">
                  <h3 className="favorite-name">{item.name || 'Unnamed Item'}</h3>
                  {item.price && (
                    <p className="favorite-price">{item.price}</p>
                  )}
                  {item.added_at && (
                    <p className="favorite-date">
                      Added {new Date(item.added_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <div className="favorite-actions">
                  <a
                    href={item.url}
                    className="shop-button"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Shop ${item.name}`}
                  >
                    Shop Now →
                  </a>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {notification && (
          <motion.div
            className={`notification ${notification.type}`}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
          >
            {notification.message}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Favorites;

